<template>
  <div class="feedback-dashboard">
    <div class="page-header">
      <div class="header-content">
        <h1><i class="fas fa-inbox"></i> Feedback Dashboard</h1>
        <p class="subtitle">Manage bug reports, feature requests, and feedback from internal users</p>
      </div>
      <button class="btn btn-primary" @click="showNewFeedback = true">
        <i class="fas fa-plus"></i>
        New Feedback
      </button>
    </div>
    
    <!-- Stats Cards -->
    <div v-if="stats" class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon new"><i class="fas fa-inbox"></i></div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.by_status?.new || 0 }}</div>
          <div class="stat-label">New</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon progress"><i class="fas fa-spinner"></i></div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.by_status?.in_progress || 0 }}</div>
          <div class="stat-label">In Progress</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon resolved"><i class="fas fa-check-circle"></i></div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.by_status?.resolved || 0 }}</div>
          <div class="stat-label">Resolved</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon total"><i class="fas fa-clipboard-list"></i></div>
        <div class="stat-content">
          <div class="stat-value">{{ stats.total || 0 }}</div>
          <div class="stat-label">Total</div>
        </div>
      </div>
    </div>
    
    <!-- Filters -->
    <div class="filters">
      <div class="filter-group">
        <label>Application</label>
        <select v-model="filters.application" @change="loadFeedback">
          <option value="">All Applications</option>
          <option value="adi">ADI / Document Review</option>
          <option value="lms">Learning Management</option>
          <option value="couples">Couples Connect</option>
          <option value="platform">Platform</option>
        </select>
      </div>
      <div class="filter-group">
        <label>Status</label>
        <select v-model="filters.status" @change="loadFeedback">
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="wont_fix">Won't Fix</option>
        </select>
      </div>
      <div class="filter-group">
        <label>Type</label>
        <select v-model="filters.type" @change="loadFeedback">
          <option value="">All Types</option>
          <option value="bug">Bug Report</option>
          <option value="feature">Feature Request</option>
          <option value="feedback">Feedback</option>
          <option value="question">Question</option>
        </select>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <span>Loading feedback...</span>
    </div>
    
    <!-- Empty State -->
    <div v-else-if="feedback.length === 0" class="empty-state">
      <i class="fas fa-inbox"></i>
      <h3>No feedback found</h3>
      <p>Try adjusting your filters or submit new feedback.</p>
    </div>
    
    <!-- Feedback List -->
    <div v-else class="feedback-list">
      <div 
        v-for="item in feedback" 
        :key="item.id" 
        class="feedback-card"
        @click="selectedFeedback = item"
      >
        <div class="card-header">
          <div class="type-badge" :class="item.feedback_type">
            <i :class="getTypeIcon(item.feedback_type)"></i>
            {{ item.feedback_type_display }}
          </div>
          <div class="status-badge" :class="item.status">
            {{ item.status_display }}
          </div>
        </div>
        
        <h3 class="card-title">{{ item.title }}</h3>
        
        <p class="card-description">{{ truncate(item.description, 150) }}</p>
        
        <div class="card-meta">
          <span class="app-tag" :class="item.application">
            {{ item.application_display }}
          </span>
          <span class="priority-badge" :class="item.priority">
            {{ item.priority }}
          </span>
          <span class="user-name">
            <i class="fas fa-user"></i>
            {{ item.user_name || 'Unknown' }}
          </span>
          <span class="date">
            <i class="fas fa-clock"></i>
            {{ formatDate(item.created_at) }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Detail Modal -->
    <FeedbackDetailModal
      v-if="selectedFeedback"
      :feedback="selectedFeedback"
      @close="selectedFeedback = null"
      @updated="handleFeedbackUpdated"
    />
    
    <!-- New Feedback Modal -->
    <FeedbackModal
      v-if="showNewFeedback"
      @close="showNewFeedback = false"
      @submitted="handleNewFeedbackSubmitted"
    />
  </div>
</template>

<script>
import observabilityAPI from '@/services/observabilityAPI';
import FeedbackModal from '@/components/FeedbackModal.vue';
import FeedbackDetailModal from '@/components/FeedbackDetailModal.vue';

export default {
  name: 'FeedbackDashboard',
  
  components: {
    FeedbackModal,
    FeedbackDetailModal,
  },
  
  data() {
    return {
      feedback: [],
      stats: null,
      loading: true,
      filters: {
        application: '',
        status: '',
        type: '',
      },
      selectedFeedback: null,
      showNewFeedback: false,
    };
  },
  
  async mounted() {
    await Promise.all([
      this.loadFeedback(),
      this.loadStats(),
    ]);
  },
  
  methods: {
    async loadFeedback() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.application) params.application = this.filters.application;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.type) params.type = this.filters.type;
        
        const data = await observabilityAPI.getFeedbackList(params);
        this.feedback = data.results || data;
      } catch (error) {
        console.error('Failed to load feedback:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async loadStats() {
      try {
        this.stats = await observabilityAPI.getFeedbackStats();
      } catch (error) {
        console.error('Failed to load stats:', error);
      }
    },
    
    getTypeIcon(type) {
      const icons = {
        bug: 'fas fa-bug',
        feature: 'fas fa-lightbulb',
        feedback: 'fas fa-comment',
        question: 'fas fa-question-circle',
      };
      return icons[type] || 'fas fa-comment';
    },
    
    truncate(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    
    handleFeedbackUpdated(updatedFeedback) {
      const index = this.feedback.findIndex(f => f.id === updatedFeedback.id);
      if (index !== -1) {
        this.feedback[index] = updatedFeedback;
      }
      this.selectedFeedback = null;
      this.loadStats();
    },
    
    handleNewFeedbackSubmitted() {
      this.showNewFeedback = false;
      this.loadFeedback();
      this.loadStats();
    },
  },
};
</script>

<style scoped>
.feedback-dashboard {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-header h1 i {
  color: #667eea;
}

.page-header .subtitle {
  margin: 8px 0 0;
  color: #718096;
  font-size: 14px;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.stat-icon.new {
  background: #ebf4ff;
  color: #667eea;
}

.stat-icon.progress {
  background: #fefcbf;
  color: #d69e2e;
}

.stat-icon.resolved {
  background: #f0fff4;
  color: #38a169;
}

.stat-icon.total {
  background: #f7fafc;
  color: #718096;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
}

.stat-label {
  font-size: 13px;
  color: #718096;
}

/* Filters */
.filters {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 12px;
  font-weight: 500;
  color: #4a5568;
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  min-width: 160px;
}

/* Loading/Empty States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #718096;
}

.loading-state i,
.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 8px;
  color: #4a5568;
}

/* Feedback List */
.feedback-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feedback-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
}

.feedback-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.type-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.type-badge.bug {
  background: #fed7d7;
  color: #c53030;
}

.type-badge.feature {
  background: #fefcbf;
  color: #d69e2e;
}

.type-badge.feedback {
  background: #c6f6d5;
  color: #276749;
}

.type-badge.question {
  background: #bee3f8;
  color: #2b6cb0;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.status-badge.new {
  background: #ebf4ff;
  color: #667eea;
}

.status-badge.acknowledged {
  background: #e9d8fd;
  color: #6b46c1;
}

.status-badge.in_progress {
  background: #fefcbf;
  color: #d69e2e;
}

.status-badge.resolved {
  background: #c6f6d5;
  color: #276749;
}

.status-badge.wont_fix {
  background: #e2e8f0;
  color: #4a5568;
}

.card-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.card-description {
  margin: 0 0 12px;
  font-size: 14px;
  color: #718096;
  line-height: 1.5;
}

.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #718096;
}

.card-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.app-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.app-tag.adi {
  background: #ebf4ff;
  color: #3182ce;
}

.app-tag.lms {
  background: #fef3c7;
  color: #92400e;
}

.app-tag.couples {
  background: #fce7f3;
  color: #9d174d;
}

.app-tag.platform {
  background: #e2e8f0;
  color: #4a5568;
}

.priority-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
  text-transform: capitalize;
}

.priority-badge.low {
  background: #f0fff4;
  color: #276749;
}

.priority-badge.medium {
  background: #fefcbf;
  color: #744210;
}

.priority-badge.high {
  background: #fed7d7;
  color: #c53030;
}

.priority-badge.critical {
  background: #742a2a;
  color: white;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .filters {
    flex-direction: column;
  }
  
  .page-header {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
